/* eslint-disable no-underscore-dangle */
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";

const API_BASE = protectedResources.apiTodoList.endpoint;

const STATUS_COLOR = {
  open: "default",
  in_progress: "warning",
  added: "success",
  rejected: "error",
};

// Confidence → MUI Alert severity (no nested ternary — CRA build forbids it).
const confSeverity = (c) => {
  if (c >= 70) return "success";
  if (c >= 40) return "warning";
  return "info";
};

/**
 * Admin queue for signpost requests raised from the AI finder's web-search step (ID96 Ph2).
 * Admins add the source to the library, then mark the request "added" — which notifies the
 * requester with a deep link to set thresholds.
 */
export default function SignpostRequests() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [libraries, setLibraries] = useState([]);
  const [linkChoice, setLinkChoice] = useState({}); // requestId -> library entry

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, libRes] = await Promise.all([
        executeRef.current("GET", `${API_BASE}/emergingRisk/signpost-requests`),
        executeRef.current("GET", `${API_BASE}/signpostLibrary/list`).catch(() => null),
      ]);
      setRows(Array.isArray(reqRes?.data) ? reqRes.data : []);
      const libs = libRes?.data || libRes;
      setLibraries(Array.isArray(libs) ? libs : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const body = { status };
      // When linking to a draft risk on "added", pass the chosen library entry id so the
      // backend creates the signpost on the requester's risk.
      if (status === "added") {
        const lib = linkChoice[id];
        const libId = lib?.id || lib?._id;
        if (libId) body.linkedLibraryId = String(libId);
      }
      await executeRef.current("PATCH", `${API_BASE}/emergingRisk/signpost-requests/${id}`, body);
      load();
    } catch {
      /* ignore */
    }
  };

  // ── AI configure: turn a request into a testable library config, review, test, add ────────────
  const SOURCE_TYPES = [
    "custom_api",
    "stock_yahoo",
    "fx_alphavantage",
    "econdb",
    "rss_feed",
    "url_scrape",
    "excel_online",
    "llm_search",
  ];
  const [ai, setAi] = useState(null); // { req, draft, meta, configText } or null
  const [aiBusy, setAiBusy] = useState(false); // drafting
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTest, setAiTest] = useState(null); // { ok, message }
  const [aiCreating, setAiCreating] = useState(false);
  const [aiError, setAiError] = useState("");

  const openAiConfigure = async (r) => {
    const id = r.id || r._id;
    setAi({ req: r, draft: null, configText: "" });
    setAiBusy(true);
    setAiTest(null);
    setAiError("");
    try {
      const res = await executeRef.current(
        "POST",
        `${API_BASE}/emergingRisk/signpost-requests/${id}/ai-config`,
        {}
      );
      const d = res?.data?.draft;
      if (!d) {
        setAiError(res?.message || "Could not draft a config for this request.");
        setAiBusy(false);
        return;
      }
      setAi({ req: r, draft: d, configText: JSON.stringify(d.dataSourceConfig || {}, null, 2) });
    } catch (e) {
      setAiError("Failed to reach the AI configurator.");
    } finally {
      setAiBusy(false);
    }
  };

  const patchDraft = (patch) =>
    setAi((cur) => (cur ? { ...cur, draft: { ...cur.draft, ...patch } } : cur));

  const parsedConfig = () => {
    try {
      return { ok: true, value: JSON.parse(ai.configText || "{}") };
    } catch {
      return { ok: false, value: null };
    }
  };

  const testDraft = async () => {
    const pc = parsedConfig();
    if (!pc.ok) {
      setAiTest({ ok: false, message: "Config is not valid JSON — fix it before testing." });
      return;
    }
    setAiTesting(true);
    setAiTest(null);
    try {
      const res = await executeRef.current("POST", `${API_BASE}/signpostLibrary/test`, {
        dataSourceType: ai.draft.dataSourceType,
        dataSourceConfig: pc.value,
      });
      const d = res?.data || {};
      const ok = d.success !== false && res?.status !== false;
      setAiTest({
        ok,
        message: ok
          ? `✓ ${d.value != null ? `Fetched value: ${d.value}` : d.note || "Source reachable."}`
          : `✗ ${d.error || res?.message || "Test failed."}`,
      });
    } catch {
      setAiTest({ ok: false, message: "✗ Test request failed." });
    } finally {
      setAiTesting(false);
    }
  };

  const addDraftToLibrary = async () => {
    const pc = parsedConfig();
    if (!pc.ok) {
      setAiError("Config is not valid JSON.");
      return;
    }
    const r = ai.req;
    const id = r.id || r._id;
    // The library only accepts hourly/daily/weekly; the AI can suggest monthly/annually (e.g. for a
    // market-research report), which the create endpoint rejects. Normalise before sending.
    const uf = String(ai.draft.updateFrequency || "").toLowerCase();
    const slow =
      ai.draft.dataSourceType === "llm_search" || ai.draft.dataSourceType === "url_scrape";
    let freq = slow ? "weekly" : "daily";
    if (["hourly", "daily", "weekly"].includes(uf)) freq = uf;
    setAiCreating(true);
    setAiError("");
    try {
      const created = await executeRef.current("POST", `${API_BASE}/signpostLibrary/create`, {
        name: ai.draft.name,
        description: ai.draft.description,
        dataSourceType: ai.draft.dataSourceType,
        dataSourceConfig: pc.value,
        unit: ai.draft.unit,
        provider: ai.draft.provider,
        updateFrequency: freq,
      });
      const libId = created?.data?.id || created?.data?._id;
      if (!libId) {
        setAiError(created?.message || "Library entry was not created.");
        setAiCreating(false);
        return;
      }
      // Link the new library entry back to the request and mark it added (notifies the requester,
      // activates their placeholder signpost — same path as the manual "Mark added").
      await executeRef.current("PATCH", `${API_BASE}/emergingRisk/signpost-requests/${id}`, {
        status: "added",
        linkedLibraryId: String(libId),
      });
      setAi(null);
      load();
    } catch {
      setAiError("Failed to create the library entry.");
    } finally {
      setAiCreating(false);
    }
  };

  let body;
  if (loading) {
    body = <CircularProgress sx={{ color: "#52009b" }} />;
  } else if (rows.length === 0) {
    body = (
      <MDTypography variant="body2" color="text.secondary">
        No signpost requests yet.
      </MDTypography>
    );
  } else {
    body = (
      <Box sx={{ border: "1px solid rgba(82,0,155,0.15)", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(82,0,155,0.06)" }}>
              {["Case", "Source", "Risk", "Requested by", "Status", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, color: "#52009b", fontSize: "0.75rem" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id || r._id} hover>
                <TableCell sx={{ fontSize: "0.78rem" }}>{r.caseNumber}</TableCell>
                <TableCell sx={{ fontSize: "0.78rem" }}>
                  <MDTypography variant="caption" fontWeight="bold" sx={{ display: "block" }}>
                    {r.name || "—"}
                  </MDTypography>
                  {r.description && (
                    <MDTypography variant="caption" color="text.secondary">
                      {r.description}
                    </MDTypography>
                  )}
                  {r.sourceUrl && (
                    <Link
                      href={
                        /^https?:\/\//.test(r.sourceUrl) ? r.sourceUrl : `https://${r.sourceUrl}`
                      }
                      target="_blank"
                      rel="noopener"
                      sx={{ display: "block", fontSize: "0.7rem" }}
                    >
                      {r.sourceUrl}
                    </Link>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: "0.78rem" }}>{r.riskTitle || "—"}</TableCell>
                <TableCell sx={{ fontSize: "0.78rem" }}>{r.requestedByName || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={(r.status || "open").replace("_", " ")}
                    color={STATUS_COLOR[r.status] || "default"}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                    {r.status !== "added" && (
                      <Autocomplete
                        size="small"
                        sx={{ width: 200 }}
                        options={libraries}
                        getOptionLabel={(o) => o?.name || ""}
                        value={linkChoice[r.id || r._id] || null}
                        onChange={(_, v) =>
                          setLinkChoice((prev) => ({ ...prev, [r.id || r._id]: v }))
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="Link library entry" />
                        )}
                      />
                    )}
                    {r.status !== "added" && (
                      <MDButton
                        size="small"
                        variant="outlined"
                        sx={{
                          color: "#52009b",
                          borderColor: "#52009b",
                          textTransform: "none",
                          "&:hover": { borderColor: "#3a0070", bgcolor: "rgba(82,0,155,0.04)" },
                        }}
                        onClick={() => openAiConfigure(r)}
                      >
                        ✨ AI configure
                      </MDButton>
                    )}
                    {r.status !== "added" && (
                      <MDButton
                        size="small"
                        variant="contained"
                        sx={{
                          bgcolor: "#52009b",
                          color: "#fff",
                          textTransform: "none",
                          "&:hover": { bgcolor: "#3a0070" },
                        }}
                        onClick={() => setStatus(r.id || r._id, "added")}
                      >
                        Mark added
                      </MDButton>
                    )}
                    {r.status === "open" && (
                      <MDButton
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: "none" }}
                        onClick={() => setStatus(r.id || r._id, "in_progress")}
                      >
                        In progress
                      </MDButton>
                    )}
                    {r.status !== "rejected" && r.status !== "added" && (
                      <MDButton
                        size="small"
                        variant="text"
                        color="error"
                        sx={{ textTransform: "none" }}
                        onClick={() => setStatus(r.id || r._id, "rejected")}
                      >
                        Reject
                      </MDButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box p={3}>
        <MDTypography variant="h5" sx={{ color: "#52009b", fontWeight: 700, mb: 1 }}>
          Signpost Requests
        </MDTypography>
        <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          New data-source requests raised by users from the AI signpost finder. Add the source to
          the Signpost Library, then mark the request &quot;Added&quot; to notify the requester.
        </MDTypography>

        {body}
      </Box>

      <Dialog open={!!ai} onClose={() => (aiCreating ? null : setAi(null))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "#52009b", fontWeight: 700 }}>
          ✨ AI configure data source
          {ai?.req?.name ? (
            <MDTypography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
              {ai.req.name}
            </MDTypography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          {aiBusy && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
              <CircularProgress size={20} sx={{ color: "#52009b" }} />
              <MDTypography variant="body2" color="text.secondary">
                Drafting a configuration from the request…
              </MDTypography>
            </Box>
          )}
          {aiError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {aiError}
            </Alert>
          )}
          {ai?.draft && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Alert severity={confSeverity(ai.draft.confidence)}>
                <b>Confidence {ai.draft.confidence != null ? `${ai.draft.confidence}%` : "—"}.</b>{" "}
                {ai.draft.rationale}
                {ai.draft.caveats ? (
                  <MDTypography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                    ⚠ {ai.draft.caveats}
                  </MDTypography>
                ) : null}
              </Alert>
              <TextField
                size="small"
                label="Name"
                value={ai.draft.name || ""}
                onChange={(e) => patchDraft({ name: e.target.value })}
              />
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 190, flex: 1 }}
                  options={SOURCE_TYPES}
                  value={ai.draft.dataSourceType || null}
                  onChange={(_, v) => patchDraft({ dataSourceType: v || ai.draft.dataSourceType })}
                  renderInput={(params) => <TextField {...params} label="Source type" />}
                />
                <TextField
                  size="small"
                  label="Unit"
                  sx={{ width: 130 }}
                  value={ai.draft.unit || ""}
                  onChange={(e) => patchDraft({ unit: e.target.value })}
                />
              </Box>
              <TextField
                size="small"
                label="Data source config (JSON)"
                multiline
                minRows={5}
                value={ai.configText}
                onChange={(e) => setAi((cur) => ({ ...cur, configText: e.target.value }))}
                InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.78rem" } }}
              />
              {aiTest && <Alert severity={aiTest.ok ? "success" : "error"}>{aiTest.message}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <MDButton variant="text" onClick={() => setAi(null)} disabled={aiCreating}>
            Cancel
          </MDButton>
          <MDButton
            variant="outlined"
            sx={{ color: "#52009b", borderColor: "#52009b", textTransform: "none" }}
            onClick={testDraft}
            disabled={!ai?.draft || aiTesting || aiCreating}
          >
            {aiTesting ? "Testing…" : "Test source"}
          </MDButton>
          <MDButton
            variant="contained"
            sx={{
              bgcolor: "#52009b",
              color: "#fff",
              textTransform: "none",
              "&:hover": { bgcolor: "#3a0070" },
            }}
            onClick={addDraftToLibrary}
            disabled={!ai?.draft || aiCreating}
          >
            {aiCreating ? "Adding…" : "Add to library & mark added"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
